import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { applicationUrls } from '../application-constants/application-urls.const';
import { ServerResponse } from '../models/dto/server-response.dto';
import { environment } from 'src/environments/environment';

export type StpInactiveReason = 'EXPIRED' | 'NOT_APPROVED';

export interface StpExpiryStatus {
  isExpired: boolean;
  reason: StpInactiveReason | null;
  expiresInDays: number | null;
  expireDate: string | null;
  // false for an STP still in first-time registration (no registration number or expiry date)
  hasRegistration: boolean;
}

export interface StpDateLimit {
  stpExpireDate: string | null;
  courseExpireDate: string | null;
  // the earlier of the two; dates after it are not allowed
  limitDate: string | null;
}

const STP_TIME_ZONE = 'Asia/Dhaka';
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Single source of truth for "is the logged-in STP expired".
 * Uses stpMain.isExpired / stpMain.expiresInDays from the backend when present,
 * otherwise falls back to stpMain.isApproved + stpMain.expireDate.
 * Rule: expired = isApproved is not true OR expireDate is missing/blank
 * OR expireDate is before today (Asia/Dhaka). The expiry day itself is still valid.
 */
@Injectable({
  providedIn: 'root',
})
export class StpExpiryService {
  private status = new BehaviorSubject<StpExpiryStatus | null>(null);
  private approvedOccupations$: Observable<any[]> | null = null;

  status$: Observable<StpExpiryStatus | null> = this.status.asObservable();

  isExpired$: Observable<boolean> = this.status$.pipe(
    map((status) => !!status?.isExpired),
    distinctUntilChanged()
  );

  // Course accreditation (course / assessment center) is blocked only when an expiry date
  // exists and has passed. A null or blank expiry date does not block it.
  isRegistrationExpired$: Observable<boolean> = this.status$.pipe(
    map((status) => this.isExpiryDatePassed(status)),
    distinctUntilChanged()
  );

  expiresInDays$: Observable<number | null> = this.status$.pipe(
    map((status) => (status ? status.expiresInDays : null)),
    distinctUntilChanged()
  );

  constructor(private httpClient: HttpClient) {}

  get isExpired(): boolean {
    return !!this.status.value?.isExpired;
  }

  get isRegistrationExpired(): boolean {
    return this.isExpiryDatePassed(this.status.value);
  }

  private isExpiryDatePassed(status: StpExpiryStatus | null): boolean {
    return !!status?.expireDate && status.reason === 'EXPIRED';
  }

  get currentStatus(): StpExpiryStatus | null {
    return this.status.value;
  }

  load(userId: number): void {
    if (userId == null || !this.isStpUser()) {
      this.clear();
      return;
    }
    this.httpClient
      .get<ServerResponse>(`${applicationUrls.user.getUserById}?userId=` + userId)
      .subscribe({
        next: (rs: ServerResponse) => {
          this.setStpMain(rs?.success ? rs.data?.stpMain : null);
        },
        error: () => {
          // Leave the last known status; the backend still enforces the rule.
        },
      });
  }

  /** Reloads the status for the logged-in user (e.g. after the backend returns 423). */
  refresh(): void {
    const userInfo = JSON.parse(localStorage.getItem('currentUserInfo') || '{}');
    if (userInfo?.id != null) {
      this.load(userInfo.id);
    }
  }

  /** Feed the stpMain object from a get-users-by-id response already in hand. */
  setStpMain(stpMain: any): void {
    // get-users-by-id can return an stpMain for non-STP users (admin, assessor...),
    // so the status is only kept for users with an STP role.
    if (!stpMain || !this.isStpUser()) {
      this.status.next(null);
      return;
    }
    this.status.next(this.buildStatus(stpMain));
  }

  /** True when the logged-in user has the STP_Master or STP_Basic role. */
  isStpUser(): boolean {
    let userInfo: any;
    try {
      userInfo = JSON.parse(localStorage.getItem('currentUserInfo') || '{}');
    } catch {
      return false;
    }
    const stpRoleIds = [environment.roles.stpMasterId, environment.roles.stpBasicId];
    const stpRoleNames = ['stp_master', 'stp_basic'];
    return (userInfo?.roles || []).some(
      (role: any) =>
        stpRoleIds.includes(role?.id) ||
        stpRoleNames.includes(String(role?.name).toLowerCase())
    );
  }

  clear(): void {
    this.status.next(null);
    this.approvedOccupations$ = null;
  }

  /**
   * Course approval expiry (validToCourse) of the logged-in STP for an occupation,
   * as YYYY-MM-DD. Uses the latest approval when the occupation was renewed.
   */
  getCourseExpireDate(tamMainId: number | null | undefined): Observable<string | null> {
    if (tamMainId == null || !this.isStpUser()) {
      return of(null);
    }
    if (!this.approvedOccupations$) {
      this.approvedOccupations$ = this.httpClient
        .get<ServerResponse>(`${applicationUrls.inspection.getAllApprovedInspectionResults}`)
        .pipe(
          map((res: ServerResponse) => (res?.success && Array.isArray(res.data) ? res.data : [])),
          catchError(() => of([])),
          shareReplay(1)
        );
    }
    return this.approvedOccupations$.pipe(
      map((occupations: any[]) =>
        occupations
          .filter((occupation) => occupation?.tamMainId === Number(tamMainId))
          .map((occupation) => this.toDhakaDate(occupation?.validToCourse))
          .filter((date): date is string => !!date)
          .sort()
          .pop() ?? null
      )
    );
  }

  /**
   * The last allowed date for training / assessment dates:
   * the earlier of the STP expiry date and the course approval expiry date.
   */
  getDateLimit(courseExpireDate: string | null): StpDateLimit {
    const stpExpireDate = this.status.value?.expireDate ?? null;
    const dates = [stpExpireDate, courseExpireDate].filter(
      (date): date is string => !!date
    );
    return {
      stpExpireDate,
      courseExpireDate,
      limitDate: dates.length ? dates.sort()[0] : null,
    };
  }

  /** True when a picked date falls after the limit (YYYY-MM-DD). */
  isAfterLimit(date: Date | string | null | undefined, limitDate: string | null): boolean {
    if (!date || !limitDate) {
      return false;
    }
    const value = date instanceof Date ? date : new Date(date);
    if (isNaN(value.getTime())) {
      return false;
    }
    const picked = `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
    return picked > limitDate;
  }

  /** True when the date is before today (Asia/Dhaka). */
  isPast(date: string | null): boolean {
    const today = this.toDhakaDate(new Date());
    return !!date && !!today && date < today;
  }

  getMessage(status: StpExpiryStatus | null = this.status.value): string {
    if (!status?.isExpired) {
      return '';
    }
    if (status.reason === 'EXPIRED' && status.expireDate) {
      return `Your STP registration expired on ${this.formatDate(status.expireDate)}. Please renew to continue.`;
    }
    return 'Your STP registration is not active.';
  }

  formatDate(date: string | null | undefined): string {
    if (!date) {
      return '';
    }
    const [year, month, day] = date.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }

  private buildStatus(stpMain: any): StpExpiryStatus {
    const expireDate = this.toDhakaDate(stpMain?.expireDate);
    const today = this.toDhakaDate(new Date());

    let expiresInDays: number | null =
      typeof stpMain?.expiresInDays === 'number' ? stpMain.expiresInDays : null;
    if (expiresInDays == null && expireDate && today) {
      expiresInDays = Math.round(
        (Date.parse(expireDate) - Date.parse(today)) / DAY_MS
      );
    }

    const isPastExpiry = expiresInDays != null && expiresInDays < 0;
    const isApproved = stpMain?.isApproved === true;

    // Same rule as the backend ActiveStpAspect: a missing or blank expireDate is blocked too.
    let isExpired: boolean;
    if (typeof stpMain?.isExpired === 'boolean') {
      isExpired = stpMain.isExpired;
    } else {
      isExpired = !isApproved || !expireDate || isPastExpiry;
    }

    let reason: StpInactiveReason | null = null;
    if (isExpired) {
      reason = isPastExpiry ? 'EXPIRED' : 'NOT_APPROVED';
    }

    return {
      isExpired,
      reason,
      expiresInDays,
      expireDate,
      hasRegistration: !!expireDate || !!stpMain?.registrationNo,
    };
  }

  /** Returns YYYY-MM-DD as the calendar date in Asia/Dhaka. */
  private toDhakaDate(value: any): string | null {
    if (value == null || value === '') {
      return null;
    }
    // Backend sends local Dhaka time without an offset ("YYYY-MM-DDTHH:mm:ss"),
    // so the date part is already the Dhaka calendar date.
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.substring(0, 10);
    }
    // Jackson may serialise LocalDateTime as [year, month, day, ...]
    if (Array.isArray(value) && value.length >= 3) {
      const [year, month, day] = value;
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
    if (typeof value === 'string' && value.trim() === '') {
      return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) {
      return null;
    }
    // en-CA formats as YYYY-MM-DD
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: STP_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  }
}
